// Service-page pricing tiers — mirrors the canonical /boka packages.
// The numbers here are duplicated from apps/web/lib/booking/translations.ts
// (which in turn matches the DB seed in
// packages/database/src/update-packages-2026-04.ts). Update all three together.

import type { Locale } from "@/lib/booking/translations";

export type PackageServiceType =
  | "transport"      // 6 tiers, vehicle (flytt, transport*)
  | "bortforsling"   // 6 tiers, vehicle (bortforsling, tömningar)
  | "bud"            // 6 tiers, vehicle (budtjänst, hemleverans)
  | "barhjalp"       // 6 tiers, no vehicle
  | "montering";     // 4 tiers (small–xl), simplePricing

type Slug = "xs" | "small" | "medium" | "large" | "xl" | "xxl";

interface TierCopy {
  slug: Slug;
  name: string;
  desc: string;
  /** Per-km price beyond the included 20 km. null = no driving (barhjalp/montering). */
  drivingPerKm: number | null;
  avgCost: string;
  image: string;
}

// Vehicle-service tiers (transport, bortforsling, bud).
const TIERS_VEHICLE_SV: TierCopy[] = [
  { slug: "xs",     name: "Extra Liten", desc: "1 person & personbil. Enstaka föremål, t.ex. en kartong eller en lampa.",                  drivingPerKm: 5,  avgCost: "595 kr",   image: "/package-xs.webp" },
  { slug: "small",  name: "Liten",       desc: "2 personer & skåpbil. 1–2 föremål, t.ex. 1 säng och 1 fåtölj.",                          drivingPerKm: 8,  avgCost: "995 kr",   image: "/package-small.webp" },
  { slug: "medium", name: "Mellan",      desc: "2 personer & skåpbil. 3–6 föremål, t.ex. 2 sängar, 1 fåtölj och 2 sopsäckar.",            drivingPerKm: 12, avgCost: "1 495 kr", image: "/package-medium.webp" },
  { slug: "large",  name: "Stor",        desc: "2 personer & skåpbil. 7–10 föremål, t.ex. 2 sängar, 2 soffor, 2 hyllor, 3 sopsäckar.",    drivingPerKm: 15, avgCost: "2 495 kr", image: "/package-large.webp" },
  { slug: "xl",     name: "Extra Stor",  desc: "2 personer & skåpbil, t.ex. tömning av ett garage. Upp till 4 h arbetstid.",            drivingPerKm: 18, avgCost: "4 995 kr", image: "/package-xl.webp" },
  { slug: "xxl",    name: "Mega Stor",   desc: "2 personer & skåpbil, t.ex. tömning av en lägenhet. Upp till 8 h arbetstid.",           drivingPerKm: 22, avgCost: "8 995 kr", image: "/package-xxl.webp" },
];

const TIERS_VEHICLE_EN: TierCopy[] = [
  { slug: "xs",     name: "Extra Small", desc: "1 person & passenger car. Single items, e.g. a box or a lamp.",                          drivingPerKm: 5,  avgCost: "SEK 595",   image: "/package-xs.webp" },
  { slug: "small",  name: "Small",       desc: "2 people & van. 1–2 items, e.g. 1 bed and 1 armchair.",                                  drivingPerKm: 8,  avgCost: "SEK 995",   image: "/package-small.webp" },
  { slug: "medium", name: "Medium",      desc: "2 people & van. 3–6 items, e.g. 2 beds, 1 armchair and 2 bin bags.",                     drivingPerKm: 12, avgCost: "SEK 1,495", image: "/package-medium.webp" },
  { slug: "large",  name: "Large",       desc: "2 people & van. 7–10 items, e.g. 2 beds, 2 sofas, 2 shelves, 3 bin bags.",               drivingPerKm: 15, avgCost: "SEK 2,495", image: "/package-large.webp" },
  { slug: "xl",     name: "Extra Large", desc: "2 people & van, e.g. emptying a garage. Up to 4 h working time.",                        drivingPerKm: 18, avgCost: "SEK 4,995", image: "/package-xl.webp" },
  { slug: "xxl",    name: "Mega Large",  desc: "2 people & van, e.g. emptying an apartment. Up to 8 h working time.",                   drivingPerKm: 22, avgCost: "SEK 8,995", image: "/package-xxl.webp" },
];

// Bärhjälp tiers — no vehicle, no per-km charge.
const TIERS_BARHJALP_SV: TierCopy[] = [
  { slug: "xs",     name: "Extra Liten", desc: "1 hjälpare. Enstaka små föremål.",                            drivingPerKm: null, avgCost: "595 kr",   image: "/package-xs.webp" },
  { slug: "small",  name: "Liten",       desc: "1 hjälpare. T.ex. några kartonger eller en stol.",            drivingPerKm: null, avgCost: "995 kr",   image: "/package-small.webp" },
  { slug: "medium", name: "Mellan",      desc: "2 hjälpare. T.ex. en soffa, säng eller byrå.",                drivingPerKm: null, avgCost: "1 495 kr", image: "/package-medium.webp" },
  { slug: "large",  name: "Stor",        desc: "2 hjälpare. Flera möbler eller skrymmande gods.",             drivingPerKm: null, avgCost: "2 495 kr", image: "/package-large.webp" },
  { slug: "xl",     name: "Extra Stor",  desc: "2 hjälpare. Tunga eller mycket skrymmande föremål.",          drivingPerKm: null, avgCost: "4 995 kr", image: "/package-xl.webp" },
  { slug: "xxl",    name: "Mega Stor",   desc: "3 hjälpare. Mycket tunga eller mycket skrymmande föremål.",   drivingPerKm: null, avgCost: "8 995 kr", image: "/package-xxl.webp" },
];

const TIERS_BARHJALP_EN: TierCopy[] = [
  { slug: "xs",     name: "Extra Small", desc: "1 helper. Single small items.",                               drivingPerKm: null, avgCost: "SEK 595",   image: "/package-xs.webp" },
  { slug: "small",  name: "Small",       desc: "1 helper. E.g. a few boxes or a chair.",                      drivingPerKm: null, avgCost: "SEK 995",   image: "/package-small.webp" },
  { slug: "medium", name: "Medium",      desc: "2 helpers. E.g. a sofa, bed or dresser.",                     drivingPerKm: null, avgCost: "SEK 1,495", image: "/package-medium.webp" },
  { slug: "large",  name: "Large",       desc: "2 helpers. Multiple pieces or bulky items.",                  drivingPerKm: null, avgCost: "SEK 2,495", image: "/package-large.webp" },
  { slug: "xl",     name: "Extra Large", desc: "2 helpers. Heavy or very bulky items.",                       drivingPerKm: null, avgCost: "SEK 4,995", image: "/package-xl.webp" },
  { slug: "xxl",    name: "Mega Large",  desc: "3 helpers. Very heavy or very bulky items.",                  drivingPerKm: null, avgCost: "SEK 8,995", image: "/package-xxl.webp" },
];

// Möbelmontering — only small–xl, simplePricing in template (no extra cols shown).
// avgCost differs from vehicle services (montering is its own price ladder).
const TIERS_MONTERING_SV: TierCopy[] = [
  { slug: "small",  name: "Liten",       desc: "2 personer. Enstaka möbler. T.ex. en bokhylla eller säng.",                                       drivingPerKm: null, avgCost: "1 495 kr", image: "/package-small.webp" },
  { slug: "medium", name: "Mellan",      desc: "2 personer. 2–3 möbler. T.ex. säng med förvaring och en byrå.",                                   drivingPerKm: null, avgCost: "2 495 kr", image: "/package-medium.webp" },
  { slug: "large",  name: "Stor",        desc: "2 personer. 4–6 möbler, ca 2–4 tim. T.ex. en garderob, en byrå, en säng och ett skrivbord.",      drivingPerKm: null, avgCost: "3 995 kr", image: "/package-large.webp" },
  { slug: "xl",     name: "Extra Stor",  desc: "2 personer. 6–10 möbler, ca 4–6 tim. T.ex. fyra garderober, två byråer, en säng och ett skrivbord.", drivingPerKm: null, avgCost: "5 995 kr", image: "/package-xl.webp" },
];

const TIERS_MONTERING_EN: TierCopy[] = [
  { slug: "small",  name: "Small",       desc: "2 people. Single pieces. E.g. a bookshelf or bed.",                                               drivingPerKm: null, avgCost: "SEK 1,495", image: "/package-small.webp" },
  { slug: "medium", name: "Medium",      desc: "2 people. 2–3 pieces. E.g. bed with storage and a dresser.",                                      drivingPerKm: null, avgCost: "SEK 2,495", image: "/package-medium.webp" },
  { slug: "large",  name: "Large",       desc: "2 people. 4–6 pieces, ~2–4h. E.g. wardrobe, dresser, bed and desk.",                              drivingPerKm: null, avgCost: "SEK 3,995", image: "/package-large.webp" },
  { slug: "xl",     name: "Extra Large", desc: "2 people. 6–10 pieces, ~4–6h. E.g. four wardrobes, two dressers, bed and desk.",                  drivingPerKm: null, avgCost: "SEK 5,995", image: "/package-xl.webp" },
];

// "Personer" column — number of crew + vehicle (when applicable).
const CREW_VEHICLE_SV: Record<Slug, string> = {
  xs: "1 pers + bil", small: "2 pers + skåpbil", medium: "2 pers + skåpbil",
  large: "2 pers + skåpbil", xl: "2 pers + skåpbil", xxl: "2 pers + skåpbil",
};
const CREW_VEHICLE_EN: Record<Slug, string> = {
  xs: "1 person + car", small: "2 people + van", medium: "2 people + van",
  large: "2 people + van", xl: "2 people + van", xxl: "2 people + van",
};
const CREW_BARHJALP_SV: Record<Slug, string> = {
  xs: "1 hjälpare", small: "1 hjälpare", medium: "2 hjälpare",
  large: "2 hjälpare", xl: "2 hjälpare", xxl: "3 hjälpare",
};
const CREW_BARHJALP_EN: Record<Slug, string> = {
  xs: "1 helper", small: "1 helper", medium: "2 helpers",
  large: "2 helpers", xl: "2 helpers", xxl: "3 helpers",
};
const CREW_MONTERING_SV: Record<Slug, string> = {
  xs: "2 personer", small: "2 personer", medium: "2 personer",
  large: "2 personer", xl: "2 personer", xxl: "2 personer",
};
const CREW_MONTERING_EN: Record<Slug, string> = {
  xs: "2 people", small: "2 people", medium: "2 people",
  large: "2 people", xl: "2 people", xxl: "2 people",
};

// `includedDistanceKm` default — kept in sync with packages/database/src/pricing.ts.
const INCLUDED_KM = 20;

function formatDistanceFee(perKm: number | null, sv: boolean): string {
  if (perKm == null) return "—";
  return sv
    ? `${INCLUDED_KM} km ingår, sedan ${perKm} kr/km`
    : `First ${INCLUDED_KM} km included, then SEK ${perKm}/km`;
}

interface PricingRow {
  name: string;
  desc: string;
  image?: string;
  alt?: string;
  /** Number of crew + vehicle (header label is "Personer"). */
  crew: string;
  /** "20 km ingår, sedan X kr/km" — driving leg from depot to pickup. */
  framkorning: string;
  /** Optional A→B leg charge (transport/bud only). */
  kmPrisAB?: string;
  avgCost: string;
}

export function getPackagePricingRows(
  serviceType: PackageServiceType,
  locale: Locale,
): PricingRow[] {
  const sv = locale === "sv";
  let tiers: TierCopy[];
  let crew: Record<Slug, string>;
  // transport + bud have an A→B leg shown in the price table; others don't.
  const showAB = serviceType === "transport" || serviceType === "bud";

  switch (serviceType) {
    case "barhjalp":
      tiers = sv ? TIERS_BARHJALP_SV : TIERS_BARHJALP_EN;
      crew = sv ? CREW_BARHJALP_SV : CREW_BARHJALP_EN;
      break;
    case "montering":
      tiers = sv ? TIERS_MONTERING_SV : TIERS_MONTERING_EN;
      crew = sv ? CREW_MONTERING_SV : CREW_MONTERING_EN;
      break;
    case "transport":
    case "bortforsling":
    case "bud":
      tiers = sv ? TIERS_VEHICLE_SV : TIERS_VEHICLE_EN;
      crew = sv ? CREW_VEHICLE_SV : CREW_VEHICLE_EN;
      break;
  }

  return tiers.map((t) => {
    const fee = formatDistanceFee(t.drivingPerKm, sv);
    return {
      name: t.name,
      desc: t.desc,
      image: t.image,
      crew: crew[t.slug],
      framkorning: fee,
      ...(showAB ? { kmPrisAB: fee } : {}),
      avgCost: t.avgCost,
    };
  });
}

// Marketing copy used as priceHint paragraph above the pricing table.
// Names + prices match /boka exactly.
export const PACKAGE_PRICE_HINT_SV: Record<PackageServiceType, string> = {
  transport:
    "Vi anpassar paketet efter ditt uppdrag. Enstaka föremål – Extra Liten (595 kr). 1–2 föremål – Liten (995 kr). 3–6 föremål – Mellan (1 495 kr). 7–10 föremål – Stor (2 495 kr). Tömning av ett garage – Extra Stor (4 995 kr). Tömning av en lägenhet – Mega Stor (8 995 kr). Körsträcka inom 20 km ingår alltid.",
  bortforsling:
    "Välj paket efter mängd. En enstaka pryl – Extra Liten (595 kr). 1–2 möbler – Liten (995 kr). 3–6 föremål – Mellan (1 495 kr). 7–10 föremål – Stor (2 495 kr). Tömning av ett rum eller garage – Extra Stor (4 995 kr). Hela lägenheten – Mega Stor (8 995 kr). Vi kör direkt till återvinningen.",
  bud:
    "Snabb leverans i alla storlekar. Småpaket – Extra Liten (595 kr). 1–2 föremål – Liten (995 kr). 3–6 föremål – Mellan (1 495 kr). 7–10 föremål – Stor (2 495 kr). Pall eller skrymmande gods – Extra Stor (4 995 kr). Hel lastbil – Mega Stor (8 995 kr).",
  barhjalp:
    "Vi bär det tunga åt dig. Enstaka små föremål – Extra Liten (595 kr). Några kartonger – Liten (995 kr). En soffa eller säng – Mellan (1 495 kr). Flera möbler – Stor (2 495 kr). Tunga eller skrymmande föremål – Extra Stor (4 995 kr). Mycket tungt eller många våningar – Mega Stor (8 995 kr).",
  montering:
    "En enstaka möbel som en bokhylla eller säng – Liten (1 495 kr). 2–3 möbler – Mellan (2 495 kr). 4–6 möbler, ca 2–4 timmar – Stor (3 995 kr). 6–10 möbler, ca 4–6 timmar – Extra Stor (5 995 kr). Vi har med verktyg och fixar allt på plats.",
};

export const PACKAGE_PRICE_HINT_EN: Record<PackageServiceType, string> = {
  transport:
    "We match the package to your job. Single items – Extra Small (SEK 595). 1–2 items – Small (SEK 995). 3–6 items – Medium (SEK 1,495). 7–10 items – Large (SEK 2,495). Garage clear-out – Extra Large (SEK 4,995). Full apartment – Mega Large (SEK 8,995). Distance within 20 km is always included.",
  bortforsling:
    "Pick the package that fits. A single item – Extra Small (SEK 595). 1–2 pieces – Small (SEK 995). 3–6 items – Medium (SEK 1,495). 7–10 items – Large (SEK 2,495). One room or garage – Extra Large (SEK 4,995). Whole apartment – Mega Large (SEK 8,995). We drive everything straight to recycling.",
  bud:
    "Fast delivery in every size. Small parcels – Extra Small (SEK 595). 1–2 items – Small (SEK 995). 3–6 items – Medium (SEK 1,495). 7–10 items – Large (SEK 2,495). Pallet or bulky goods – Extra Large (SEK 4,995). Full van – Mega Large (SEK 8,995).",
  barhjalp:
    "We carry the heavy stuff for you. Single small items – Extra Small (SEK 595). A few boxes – Small (SEK 995). A sofa or bed – Medium (SEK 1,495). Multiple pieces – Large (SEK 2,495). Heavy or bulky items – Extra Large (SEK 4,995). Very heavy or many floors – Mega Large (SEK 8,995).",
  montering:
    "A single piece like a bookshelf or bed – Small (SEK 1,495). 2–3 pieces – Medium (SEK 2,495). 4–6 pieces, ~2–4 hours – Large (SEK 3,995). 6–10 pieces, ~4–6 hours – Extra Large (SEK 5,995). We bring tools and assemble on site.",
};
